import { FetchUtil } from "../util/FetchUtil";
import { IUserActivityFilterResponse } from "../models/UserActivity/IUserActivityFilterResponse";
import { IUserActivityModel } from "../models/UserActivity/IUserActivityModel";
import { IUserActivityServiceEndpoint } from "../models/UserActivity/IUserActivityServiceEndpoint";
import { IUserActivityViewModel } from "../models/UserActivity/IUserActivityViewModel";
import { IUserProfile } from "../@types/Wyanoke/UserProfile"

import { StringUtil } from "../util/StringUtil";
import { UrlUtil } from "../util/UrlUtil";
import { UserActivityModel } from "../models/UserActivity/UserActivityModel";
import { WyanokeUtil } from "../util/WyanokeUtil";

type RawPromise = Promise<Readonly<IUserActivityModel> | null> | null;

interface IRawFilterResponse {
  readonly activity?: {
    readonly activityId?: string;
    readonly expires?: string;
    readonly providers?: ReadonlyArray<{
      providerid?: string;
      title?: string;
    }>;
    readonly title?: string;
    readonly url?: string;
    readonly lmsname?: string;
    readonly isexternal?: boolean;
  };

  readonly certificate?: {
    readonly credits?: number;
    readonly creditType?: string;
    readonly creditTypeInfo?: string;
    readonly data? : ReadonlyArray <{
      name?: string;
      value?:string;
    }>;
    readonly date?: string;
  };
}

export class UserActivityService {
  private _currentStatePromise: Promise<IUserActivityViewModel> | null = null;
  private _rawStatePromise: RawPromise = null;
  private _updatePromise: Promise<void> | null = null;

  constructor(private readonly _api: IUserActivityServiceEndpoint) {}

  private static findActiveStepUrl(
    state: IUserActivityViewModel
  ): string | null {
    let lastCompletedStepIdx = -1;
    state.steps.every((step, idx) => {
      if (step.complete) {
        lastCompletedStepIdx = idx;

        return true;
      }

      return false;
    });

    const activeStepIdx = Math.min(
      state.steps.length - 1,
      lastCompletedStepIdx + 1
    );

    const activeStep = state.steps[activeStepIdx];

    if (!activeStep) {
      console.warn("Could not locate the user's active step.");
      const firstStep = state.steps[0];

      return firstStep ? firstStep.url : null;
    }

    return activeStep.url;
  }

  public async getActivityStatus(activityId: string): Promise<IUserActivityViewModel | null> {
    const profile = await WyanokeUtil.loadUserProfile(true, false);
    if(!profile){
      return null;
    }

    const contactId = profile.contactid || "";

    const endpoint = this._api.load;

    if (!endpoint) {
      throw new Error("Could not resolve endpoint");
    }

    const values = {
      activityId,
      contactId,
      encodedActivityId: encodeURIComponent(activityId),
      encodedContactId: encodeURIComponent(contactId)
    };

    const url = StringUtil.supplant(endpoint.url, values) || "";

    const raw = (await FetchUtil.sendMessage({
      method: endpoint.method,
      url
    }) as (IUserActivityViewModel)[]);

    return raw[0];
  }

  public async filterUserActivities(
    _creditType: string | null | undefined,
    _year: string | null | undefined,
    _startDate: string | null | undefined,
    _endDate: string | null | undefined
  ): Promise<ReadonlyArray<IUserActivityFilterResponse>> {
    const api = this._api;
    const endpoint = api ? api.filter : null;
    if (!endpoint) {
      throw new Error("Could not resolve endpoint");
    }

    const profile = await WyanokeUtil.loadUserProfile(true, false);
    if (!profile) {
      throw new Error("Could not resolve user.");
    }

    const query = [
      { name: "contactId", value: profile.contactid || "" },
      { name: "year", value: _year ? _year : "" },
      { name: "creditType", value: _creditType ? _creditType  : "" },
      { name: "startdate", value: _startDate ? _startDate : "" },
      { name: "endDate", value: _endDate ? _endDate : "" }
    ]
      .filter(nvp => nvp.value)
      .map(nvp => `${nvp.name}=${encodeURIComponent(nvp.value)}`)
      .join("&");

    const queryString = query.length ? "?" + query : "";

    const creditType = _creditType ? _creditType : "";
    const year = _year ? _year : "";
    const values = {
      creditType,
      encodedCreditType: encodeURIComponent(creditType),
      encodedYear: encodeURIComponent(year),
      query: queryString,
      year
    };

    const url = StringUtil.supplant(endpoint.url, values);
    if (!url) {
      throw new Error("Invalid endpoint");
    }

    let result;
    try{
      result = await FetchUtil.sendMessage({
        method: endpoint.method,
        url
      });
    }catch{
      result = undefined;
    }

    const err = result as { response: { message: string } };
    if (err.response && err.response.message) {
      const msg = "Unexpected response from server: " + err.response.message;
      console.error(msg, result);
      throw new Error(msg);
    }

    if (!Array.isArray(result)) {
      const msg = "Unexpected response from server";
      console.error(msg, result);
      throw new Error(msg);
    }

    const resolveCertificateUrl = (raw: IRawFilterResponse) => {
      const activity = raw.activity || { activityId: "" };
      const activityId = activity.activityId || "";
      const certEndpoint = this._api.generatecertificate || { url: "" };
      const certUrl = certEndpoint.url;

      return activityId && certUrl ?
        UrlUtil.setQueryString(certUrl, "activityId", activityId) :
        null;
    };

    return result.filter(Boolean).map(raw => {
      const item = raw as IRawFilterResponse;
      const activity = item.activity || {};
      const certificate = item.certificate || {};
      const providers = activity.providers
      ? activity.providers
        .filter(Boolean)
        .map(p=>({Id: p.providerid, Title: p.title}))
      : [] as {Id?: string; Title?: string}[];

      const dateEarned = certificate.date
        ? new Date(Date.parse(certificate.date))
        : undefined;

      const expiredOn = activity.expires
        ? new Date(Date.parse(activity.expires))
        : undefined;

      let isExpired = false;
      if (expiredOn){
        let now = new Date(new Date().getUTCFullYear(),new Date().getUTCMonth(),new Date().getUTCDate());
        let expDate = new Date(expiredOn.getUTCFullYear(),expiredOn.getUTCMonth(),expiredOn.getUTCDate());
        isExpired = expDate < now
      }

      return {
        activityId: activity.activityId,
        activityTitle: activity.title,
        lmsname: activity.lmsname,
        activityUrl: activity.url,
        certificateUrl: resolveCertificateUrl(item),
        creditNotice: certificate.creditTypeInfo,
        creditType: certificate.creditType,
        credits: certificate.credits,
        data: certificate.data,
        dateEarned,
        isExpired,
        providers,
        isExternal: activity.isexternal
      };
    });
  }

  public async currentState(): Promise<IUserActivityViewModel> {
    const cachedPromise = this._currentStatePromise;

    if (cachedPromise) {
       await cachedPromise;
    }

    return cachedPromise
      ? cachedPromise
      : (this._currentStatePromise = this.loadCurrentState());
  }

  public async removeUserActivity(
    contactId: string,
    activityId: string
  ): Promise<boolean> {
    const endpoint = this._api.remove;

    if (!endpoint) {
      throw new Error("Could not resolve endpoint");
    }

    const values = {
      activityId,
      contactId,
      encodedActivityId: encodeURIComponent(activityId),
      encodedContactId: encodeURIComponent(contactId)
    };

    const url = StringUtil.supplant(endpoint.url, values) || "";

    try{
      await FetchUtil.sendMessage({
        method: endpoint.method,
        url
      });
    }catch{
      console.error("Error removing user activity");
    }

    return true;
  }

  public async removeUserActivityNotifications(
    contactId: string,
    activityId: string
  ): Promise<boolean> {
    const endpoint = this._api.removenotification;

    if (!endpoint) {
      throw new Error("Could not resolve endpoint");
    }

    const values = {
      activityId,
      contactId,
      encodedActivityId: encodeURIComponent(activityId),
      encodedContactId: encodeURIComponent(contactId)
    };

    const url = StringUtil.supplant(endpoint.url, values) || "";

    try{
      await FetchUtil.sendMessage({
        method: endpoint.method,
        url
      });
    }catch{
      console.error("Error removing user activity");
    }

    return true;
  }

  public async saveCredits(
    credits: number,
    creditType: string | null,
    certTemplate: string | null,
    creditTypeInfo: string | null,
    dataArr: ReadonlyArray<{ name: string; value: string }>
  ): Promise<void> {
    const updateInProgress = this._updatePromise;
    if (updateInProgress) {
      await updateInProgress;

      return this.saveCredits(
        credits,
        creditType,
        certTemplate,
        creditTypeInfo,
        dataArr
      );
    }

    return (this._updatePromise = this.saveCertificateUpdate( {
        certpath: null,
        credits,
        credittype: creditType,
        credittypeinfo: creditTypeInfo,
        data: dataArr,
        date: WyanokeUtil.GetDateISO8601(),
        templateid: certTemplate
      })
    );
  }

  public async startActivityStep(stepId: string): Promise<void> {
    console.info("Getting current state...");
    const state = await this.currentState();
    console.info("Finding step...");
    const stepIdx = state.steps.findIndex(step => step.stepid === stepId);
    if (stepIdx === -1) {
      const msg = `Could not locate step ${stepId} in activity.`;
      throw new Error(msg);
    }

    const currentStep = state.steps[stepIdx];
    if (currentStep.complete) {
      console.warn("Step already complete");

      return;
    }

    return this.updateSteps(stepId, false);
  }

  public async completeActivityStep(stepId: string): Promise<void> {
    console.info("Getting current state...");
    const state = await this.currentState();
    console.info("Finding step...");
    const stepIdx = state.steps.findIndex(step => step.stepid === stepId);
    if (stepIdx === -1) {
      const msg = `Could not locate step ${stepId} in activity.`;
      throw new Error(msg);
    }

    const currentStep = state.steps[stepIdx];
    if (currentStep.complete) {
      console.warn("Step already complete");

      return;
    }

    return this.updateSteps(stepId, true);
  }

  public async activityStepRedirectTarget(
    currentStepId: string
  ): Promise<string | null> {
    const state = await this.currentState();

    // User is only allowed to view this page if the PREVIOUS state was
    // Complete.  Otherwise, they are redirected to the last active state.

    const currentStepIdx = state.steps.findIndex(
      step => step.stepid === currentStepId
    );

    if (currentStepIdx === -1) {
      // Current step not found in list of steps.
      // User stored step data before this step was added.
      // Send them back to their active step.
      return UserActivityService.findActiveStepUrl(state);
    }

    const currentStep = state.steps[currentStepIdx];

    if (currentStep.complete) {
      // User has finished this step and can see it. Since the step has already
      // been marked as complete, there is nothing to do.
      return null;
    }

    const previousStep =
      currentStepIdx > 0 ? state.steps[currentStepIdx - 1] : null;

    if (previousStep === null) {
      // This is the first step
      // The user is allowed to see the current step.
      return null;
    }

    if (window.Activity && window.Activity.Price
      && window.Activity.Price !== 0) {
      const profile = await WyanokeUtil.loadUserProfile(true, true);
      if (profile) {
        let hasPurchased = false;
        const activityId = window.Activity &&
          window.Activity.Id ? window.Activity.Id : "";
        if(profile.purchased && activityId) {
          hasPurchased = profile.purchased.some(p => {
            return p.toLowerCase() === activityId.toLowerCase();
          });
        }

        if (hasPurchased || (profile.hasjrnlaccess
          && profile.hasjrnlaccess.toLowerCase() === "true")){
            // User either has journal access or has payed for the activity
            return null;
        }

        return state.steps[0].url;
      }
    }

    if (previousStep.complete){
      // user has completed the previous step
      return null;
    }

    // User cannot view this page because the previous step isn't marked as
    // complete.  Send them to their last active step, or the first step if
    // they don't have an active step.
    return UserActivityService.findActiveStepUrl(state);
  }

  private resetPromises() : void {
    this._currentStatePromise = null;
    this._rawStatePromise = null;
    this._updatePromise = null;
  }

  private async updateSteps(activeStepId: string | null, setComplete: boolean): Promise<void> {
    const updateInProgress = this._updatePromise;
    if (updateInProgress) {
      console.info("Update in progress...");
      await updateInProgress;

      console.info("Update complete");

      return this.updateSteps(activeStepId, setComplete);
    }
    console.info("Returning update lambda...");

    let isOverviewStep = false;
    if(window && window.Activity && window.Activity.Steps
      && window.Activity.CurrentStepId) {
    const currentStep = window.Activity.Steps.find((step: any) =>
      step.Id === activeStepId);
      isOverviewStep = currentStep && currentStep.Type
      //master class polls are the first step, overviews are second
      //cme podcast series can have poll, pretest, or podcast step first
      ? currentStep.Type.toLowerCase().indexOf("overview") > -1
        || currentStep.Type.toLowerCase() === "healio master class poll"
        || currentStep.Type.toLowerCase().indexOf("outcome survey") > -1
        || currentStep.Type.toLowerCase().indexOf("cme podcast") > -1
      : isOverviewStep;
    }

    if(!isOverviewStep || setComplete) {
      return this._updatePromise = this.saveStepUpdate(activeStepId, setComplete);
    }

    return (this._updatePromise = this.saveState((model, user, setcomplete) => {
      UserActivityModel.updateFromContext(model, user);

      const userActivity = model.useractivity || {};
      const steps = userActivity.steps || [];

      if (!steps) {
        throw new Error("No steps have been set.");
      }

      steps.forEach((step, index) => {
        if (step && step.stepid === activeStepId) {
          userActivity.steps[index].complete = setcomplete;
          userActivity.steps[index].timestamp = (new Date(Date.now())).toString();
        }
      });
    }, setComplete));
  }

  private async rawState(): Promise<Readonly<IUserActivityModel> | null> {
    const cachedPromise = this._rawStatePromise;

    return cachedPromise
      ? cachedPromise
      : (this._rawStatePromise = this.loadRawState());
  }

  private async saveCertificateUpdate(
    certificate : {
      credits: any;
      credittype: string | null;
      credittypeinfo: string | null;
      certpath: string | null;
      data: ReadonlyArray<{ name: string; value: string }>;
      date: string;
      templateid: string | null;
    }
  ): Promise<void> {
    const profile = await WyanokeUtil.loadUserProfile(true, false);

    if (!profile) {
      return;
    }

    const userActivity = (await this.rawState()) ||
      new UserActivityModel(profile);
      const timestamp = new Date().getTime();
      const certFileName = userActivity.useractivity.user.contactid.replace("-", "").toLowerCase() +
                                      "_" +
                                      timestamp.toString();

      //const certFileNameHash = CryptoUtil.sha1Encrypt(certFileCombined).toString();

      certificate.certpath = (certFileName + ".pdf");

      const body =  {
        activityid : userActivity.useractivity.activity.activityid,
        certificate,
        contactid : userActivity.useractivity.user.contactid
      };

    await Promise.all([
      FetchUtil.sendMessage(this._api.updatecertificate, body)
    ]);

    this.resetPromises();
  }

  private async saveState(
    updateFn: (model: IUserActivityModel, user: IUserProfile, setcomplete: boolean) => void,
    setComplete: boolean
  ): Promise<void> {
    const profile = await WyanokeUtil.loadUserProfile(true, false);

    if (!profile) {
      return;
    }

    const userActivity = ( await this.rawState() ||new UserActivityModel(profile));

    userActivity.useractivity.lastviewed = (new Date(Date.now())).toString();
    updateFn(userActivity, profile, setComplete);

    await Promise.all([
      FetchUtil.sendMessage(this._api.save, userActivity)
    ]);

    this.resetPromises();
  }

  private async saveStepUpdate(activeStepId: string | null, setComplete: boolean): Promise<void> {
    const profile = await WyanokeUtil.loadUserProfile(true, false);

    if (!profile) {
      return;
    }

    const userActivity = (await this.rawState()) ||
     new UserActivityModel(profile);

    let currentStep =
    userActivity.useractivity.steps.find(step => step.stepid === activeStepId);
    if(!currentStep && window.Activity && window.Activity.Steps) {
      console.warn("Couldn't find step in user's steps. Creating from window Activity Step");
      const currentActivityStep =
      window.Activity.Steps.find((step : any) => step.Id === activeStepId);
      if(!currentActivityStep) {

        return;
      }

      currentStep =  {
        complete: false,
        stepid:    currentActivityStep.Id ? currentActivityStep.Id : "",
        timestamp: (new Date(Date.now())).toString(),
        title: currentActivityStep.Title,
        type: currentActivityStep.Type,
      };
    }  else if(!currentStep) {
      console.warn("Unable to update current step");

      return;
    }
    currentStep.complete = setComplete;
    currentStep.timestamp = (new Date(Date.now())).toString();

    const body =  {
      activityid : userActivity.useractivity.activity.activityid,
      contactid : userActivity.useractivity.user.contactid,
      step : currentStep
    };

    await Promise.all([
      FetchUtil.sendMessage(this._api.updatestep, body)
    ]);

    this.resetPromises();
  }

  private async loadCurrentState(): Promise<IUserActivityViewModel> {
    console.info("Loading current state...");
    const updateInProgress = this._updatePromise;
    if (updateInProgress) {
      console.info("Update in progress, awaiting...");
      await updateInProgress;
      console.info("Update complete");

      return this.loadCurrentState();
    }
    console.info("Returning state as view model...");

    const viewModel = UserActivityModel.transformToViewModel(await this.rawState());

    this.updateStartButtonForCompletedSteps(viewModel);

    return viewModel;
  }


  private updateStartButtonForCompletedSteps(viewModel: IUserActivityViewModel): void {
    try {
      if (!viewModel || !viewModel.steps || !viewModel.steps.length) {
        return;
      }

      const activityId = window.Activity && window.Activity.Id ? window.Activity.Id : "";
      if (!activityId) {
        return;
      }

      const completedSteps = viewModel.steps.filter(step => step.complete);

      if (completedSteps.length > 0) {
        const buttonId = `start-button-${activityId}`;

        const startButton = document.getElementById(buttonId);
        if (startButton) {
          startButton.textContent = 'Resume Activity';
        } else {

          setTimeout(() => {
            const retryButton = document.getElementById(buttonId);
            if (retryButton) {
              retryButton.textContent = 'Resume Activity';
            } else {
              console.info("Button not found");
            }
          }, 1000);
        }
      } else {
          console.info("No completed steps found");
      }
    } catch (error) {
      console.error("Error updating start button text:", error);
    }
  }

  private async loadRawState(): Promise<Readonly<IUserActivityModel> | null> {
    const activity = window.Activity;
    if (!activity || !activity.Id) {
      console.warn("No global activity context.");

      return null;
    }

    const profile = await WyanokeUtil.loadUserProfile(true, false);
    if (!profile) {
      return null;
    }

    const contactId = profile.contactid || "";
    const activityId = activity.Id;

    const endpoint = this._api.load;

    if (!endpoint) {
      throw new Error("Could not resolve endpoint");
    }

    const values = {
      activityId,
      contactId,
      encodedActivityId: encodeURIComponent(activityId),
      encodedContactId: encodeURIComponent(contactId)
    };

    const url = StringUtil.supplant(endpoint.url, values) || "";

    const raw = (await FetchUtil.sendMessage({
      method: endpoint.method,
      url
    })) as (IUserActivityModel["useractivity"])[];

    if (!Array.isArray(raw)) {
      const msg = "Unexpected response from server";
      console.warn(msg, raw);
      throw new Error(msg);
    }

    const useractivity = raw[0];

    return useractivity ? { useractivity } : null;
  }
}
